/**
 * FramesTab — the asset page's "Escenas" tab (owner requests 2026-10-13, errors.md E-66 and E-67).
 *
 * One list, no grids. For an asset with a v2 run it reads `mediaAnalysis.queries.listRunFrames` and shows:
 *
 *   1. the extract funnel, the stages a decoded frame goes through (CONTRACTS.md section 8.1, step 6):
 *        candidatos (decoded, one per GOP keyframe) → tomas (shot cuts) → seleccionados (keyframes under the
 *        profile's hourly budget) → supervivientes (after the near-duplicate fold; what the understand stage
 *        analysed) → escenas (the partition; one thumbnail each);
 *   2. a vertical list of scenes, each collapsed (the console's chevron toggle), opening onto a vertical sublist
 *        of its analysed frames: one row per frame with its picture, time, caption (the description, prose or
 *        template), on-screen text, labels and caption tier.
 *
 * For an asset WITHOUT a v2 run the tab falls back to the v1 scenes as a list (SceneList), so nothing the old
 * Escenas tab showed is lost. A frame persisted before contract 1.2.0 has no picture and the row says so.
 *
 * Honest states: query loading → skeleton; no v2 run → the v1 list (its own skeleton / empty state with the CTA);
 * a run without survivors (still running, or failed before understand) → says which; the query throwing →
 * error panel + toast.
 */
import React, { Component, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { Btn, EmptyState, Icon, Panel, Pill, SkeletonList, useToast } from '../../components/primitives';
import { fmtClock } from '../../lib/format';
import { SceneList } from './SceneList';

// `api.mediaAnalysis` is typed through the generated bindings; the loose cast keeps this file free of the
// generated types' churn, the same way TranscriptTab reads the same module.
const mediaAnalysisApi: any = (api as any).mediaAnalysis;

type FrameView = {
  _id: string;
  frameIndex: number;
  sceneIndex: number;
  timeMs: number;
  ocrText: string | null;
  sceneLabels: string[] | null;
  tier: 'prose' | 'template' | 'index_only';
  caption: string | null;
  captionSource: 'model' | 'template' | null;
  escalated: boolean;
  thumbnailUrl?: string | null;   // contract 1.2.0 (E-67): the survivor's own picture; absent for older runs
};
type SceneFramesView = { _id: string; index: number; startMs: number; endMs: number; durationMs: number; thumbnailUrl: string | null; frames: FrameView[] };
type Funnel = { candidates: number | null; shots: number | null; selected: number | null; survivors: number | null; scenes: number | null };
type RunFramesAnswer = {
  runId: string | null;
  status: 'running' | 'completed' | 'failed' | 'cancelled' | null;
  profile: string | null;
  funnel: Funnel;
  scenes: SceneFramesView[];
  unassigned: FrameView[];
  truncated: boolean;
};

const isConvexId = (s: unknown): s is string => typeof s === 'string' && /^[a-z0-9]{20,}$/i.test(s);

const TIER_LABEL: Record<FrameView['tier'], string> = { prose: 'Prosa', template: 'Plantilla', index_only: 'Solo índice' };
const TIER_TONE: Record<FrameView['tier'], 'green' | 'neutral' | 'warn'> = { prose: 'green', template: 'neutral', index_only: 'warn' };

const FUNNEL_STEPS: Array<{ key: keyof Funnel; label: string; hint: string }> = [
  { key: 'candidates', label: 'Candidatos', hint: 'fotogramas decodificados, uno por keyframe del códec' },
  { key: 'shots', label: 'Tomas', hint: 'cortes de toma confirmados' },
  { key: 'selected', label: 'Seleccionados', hint: 'fotogramas clave bajo el presupuesto por hora del perfil' },
  { key: 'survivors', label: 'Supervivientes', hint: 'tras plegar los casi duplicados: los que se analizaron' },
  { key: 'scenes', label: 'Escenas', hint: 'la partición; una miniatura por escena' },
];

export type FramesTabProps = { assetId: string; onSeekMs?: (ms: number) => void };

/** A render-time error from `useQuery` lands here: an error panel plus a toast, never a fabricated empty list. */
class FramesBoundary extends Component<{ children: ReactNode; onError: (e: Error) => void }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    this.props.onError(error);
  }
  render() {
    if (this.state.error) {
      return (
        <Panel title="Escenas">
          <EmptyState
            icon="alertTriangle"
            title="No se pudieron leer las escenas"
            body={this.state.error.message}
            action={<Btn variant="solid" icon="refresh" onClick={() => window.location.reload()}>Reintentar</Btn>}
          />
        </Panel>
      );
    }
    return this.props.children;
  }
}

export function FramesTab(props: FramesTabProps): React.ReactElement {
  const { toast } = useToast();
  return (
    <FramesBoundary onError={(e) => toast({ variant: 'error', message: `No se pudieron leer las escenas: ${e.message}` })}>
      <FramesTabInner {...props} />
    </FramesBoundary>
  );
}

function FunnelStrip({ funnel }: { funnel: Funnel }) {
  return (
    <ol data-testid="frames-funnel" style={{ listStyle: 'none', margin: '0 0 16px', padding: 0, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {FUNNEL_STEPS.map((step, i) => {
        const value = funnel[step.key];
        return (
          <li key={step.key} title={step.hint} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ border: '1px solid var(--border-cool)', borderRadius: 10, padding: '8px 12px', minWidth: 112, background: 'var(--lightest-gray)', color: 'var(--ink)' }}>
              <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{step.label}</div>
              <div style={{ fontSize: 20, fontWeight: 600 }}>{value === null ? '—' : value}</div>
            </div>
            {i < FUNNEL_STEPS.length - 1 && <Icon name="chevronR" size={14} color="var(--muted-slate)" />}
          </li>
        );
      })}
    </ol>
  );
}

// One frame per row: the picture on the left, the facts on the right.
function FrameCard({ frame, onSeekMs }: { frame: FrameView; onSeekMs?: (ms: number) => void }) {
  const description = frame.caption ?? null;
  const time = fmtClock(frame.timeMs);
  return (
    <li data-testid="frame-card" style={{ border: '1px solid var(--border-cool)', borderRadius: 10, padding: 8, background: 'var(--lightest-gray)', color: 'var(--ink)', display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0, overflowWrap: 'anywhere' }}>
      <div style={{ width: 160, aspectRatio: '16 / 9', flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {frame.thumbnailUrl
          ? <img src={frame.thumbnailUrl} alt={`Fotograma ${time}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: onSeekMs ? 'pointer' : undefined }} onClick={onSeekMs ? () => onSeekMs(frame.timeMs) : undefined} />
          : <span data-testid="frame-no-image" style={{ fontSize: 10, color: 'var(--muted-slate)', padding: 6, textAlign: 'center' }}>Sin imagen publicada (anterior al contrato 1.2.0)</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        {onSeekMs
          ? <button type="button" className="t-mono" onClick={() => onSeekMs(frame.timeMs)} style={{ fontSize: 12, background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--accent)' }}>{time}</button>
          : <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{time}</span>}
        <span style={{ display: 'flex', gap: 6 }}>
          <Pill tone={TIER_TONE[frame.tier]} size="sm">{TIER_LABEL[frame.tier]}</Pill>
          {frame.escalated && <Pill tone="warn" size="sm">Escalado</Pill>}
        </span>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>
        {description
          ? description
          : <span style={{ color: 'var(--muted-slate)' }}>Sin descripción: este fotograma solo entró al índice.</span>}
      </div>
      {frame.ocrText && (
        <div style={{ fontSize: 12, color: 'var(--body-muted)' }}>
          <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Texto en pantalla · </span>{frame.ocrText}
        </div>
      )}
      {frame.sceneLabels && frame.sceneLabels.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {frame.sceneLabels.map((l) => <Pill key={l} tone="neutral" size="sm">{l}</Pill>)}
        </div>
      )}
      </div>
    </li>
  );
}

// A scene is a collapsed list item: the chevron pattern of the console (pending/_bundle.tsx ConnectionTestTool);
// its sublist, the survivors, opens on click.
function SceneGroup({ scene, onSeekMs }: { scene: SceneFramesView; onSeekMs?: (ms: number) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <li data-testid="frames-scene" style={{ marginBottom: 10, listStyle: 'none' }}>
      <div role="button" tabIndex={0} aria-expanded={open} data-testid="frames-scene-toggle"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
        style={{ display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer', padding: '6px 0' }}>
        <Icon name={open ? 'chevronD' : 'chevronR'} size={16} color="var(--muted-slate)" />
        <div style={{ width: 96, aspectRatio: '16 / 9', borderRadius: 8, overflow: 'hidden', background: 'var(--lightest-gray)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {scene.thumbnailUrl
            ? <img src={scene.thumbnailUrl} alt={`Escena ${scene.index + 1}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <Icon name="film" size={18} color="var(--muted-slate)" />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Escena {scene.index + 1}</div>
          <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>
            {fmtClock(scene.startMs)}–{fmtClock(scene.endMs)} · {scene.frames.length} {scene.frames.length === 1 ? 'fotograma' : 'fotogramas'}
          </div>
        </div>
      </div>
      {open && (
        scene.frames.length === 0
          ? <div style={{ fontSize: 12, color: 'var(--muted-slate)', padding: '4px 0 8px 28px' }}>Ningún superviviente cayó en esta escena.</div>
          : (
            <ul data-testid="frames-scene-list" style={{ listStyle: 'none', margin: 0, padding: '4px 0 8px 28px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {scene.frames.map((f) => <FrameCard key={f._id} frame={f} onSeekMs={onSeekMs} />)}
            </ul>
          )
      )}
    </li>
  );
}

function FramesTabInner({ assetId, onSeekMs }: FramesTabProps): React.ReactElement {
  const valid = isConvexId(assetId);
  const data = useQuery(mediaAnalysisApi.queries.listRunFrames, valid ? ({ assetId } as never) : 'skip') as RunFramesAnswer | undefined;
  const [noticed, setNoticed] = useState(false);
  useEffect(() => { setNoticed(false); }, [assetId]);

  if (!valid) {
    return (
      <Panel title="Escenas">
        <EmptyState icon="alertTriangle" title="Identificador de video inválido" body="No se pueden consultar las escenas para este identificador." />
      </Panel>
    );
  }
  if (data === undefined) {
    return (
      <Panel title="Escenas">
        <SkeletonList rows={6} />
      </Panel>
    );
  }
  if (data.runId === null) {
    // No v2 run: the v1 scenes as a list (SceneList carries its own skeleton, empty state and CTA).
    return (
      <Panel title="Escenas">
        <SceneList assetId={assetId} />
      </Panel>
    );
  }

  const total = data.scenes.reduce((n, s) => n + s.frames.length, 0) + data.unassigned.length;
  const subtitle = data.status === 'completed'
    ? null
    : data.status === 'running'
      ? 'El análisis sigue en curso; la lista crece a medida que se persisten los fotogramas.'
      : `El análisis terminó en estado ${data.status}; se muestran los fotogramas que alcanzaron a persistirse.`;

  return (
    <Panel
      title="Escenas"
      right={<Pill tone="neutral" size="sm">{data.scenes.length} {data.scenes.length === 1 ? 'escena' : 'escenas'} · {total} {total === 1 ? 'fotograma' : 'fotogramas'}{data.profile ? ` · perfil ${data.profile}` : ''}</Pill>}
    >
      <FunnelStrip funnel={data.funnel} />
      {subtitle && <div style={{ fontSize: 12, color: 'var(--body-muted)', marginBottom: 12 }}>{subtitle}</div>}
      {data.truncated && !noticed && (
        <div style={{ fontSize: 12, color: 'var(--body-muted)', background: 'var(--lightest-gray)', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
          Lista truncada: se muestran los primeros fotogramas; el embudo indica cuántos hay en total.
          <Btn variant="ghost" onClick={() => setNoticed(true)}>Entendido</Btn>
        </div>
      )}
      <div style={{ fontSize: 12, color: 'var(--muted-slate)', marginBottom: 14 }}>
        Una lista por escena, plegada; ábrela para ver sus fotogramas con imagen, momento, descripción, texto en pantalla y etiquetas.
      </div>
      {total === 0
        ? <EmptyState icon="grid" title="Sin fotogramas persistidos" body={data.status === 'running' ? 'La etapa de comprensión aún no ha escrito fotogramas.' : 'Este análisis no dejó fotogramas: falló antes de la etapa de comprensión.'} />
        : (
          <>
            <ul data-testid="frames-scenes" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {data.scenes.map((s) => <SceneGroup key={s._id} scene={s} onSeekMs={onSeekMs} />)}
            </ul>
            {data.unassigned.length > 0 && (
              <section data-testid="frames-unassigned">
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Sin escena asignada</div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.unassigned.map((f) => <FrameCard key={f._id} frame={f} onSeekMs={onSeekMs} />)}
                </ul>
              </section>
            )}
          </>
        )}
    </Panel>
  );
}
